import CurrencyAPI from '@everapi/currencyapi-js';

import env from '@/configs/env';

interface Key {
  from: string;
  to: string;
}

interface Value {
  rate: number;
  expiresAt: Date;
}

const cache: Map<Key, Value> = new Map();
const defaultTTL: number = 1000 * 60 * 60 * 24; // 1 day

const getFromCache = (from: string, to: string): Value | undefined => {
  const key: Key = { from, to };
  const value = cache.get(key);
  if (value && value.expiresAt > new Date()) {
    return value;
  } else if (value) {
    cache.delete(key);
  }
  return undefined;
};

const setInCache = (from: string, to: string, rate: number): void => {
  const key: Key = { from, to };
  const value: Value = {
    rate,
    expiresAt: new Date(Date.now() + defaultTTL),
  };
  cache.set(key, value);
};

export default async function getCurrencyConversionApi(
  from: string,
  to: string,
  amount: number
): Promise<{ currency: string; amount: number }> {
  if (from === to) {
    return {
      currency: to,
      amount,
    };
  }

  const cachedValue: Value | undefined = getFromCache(from, to);
  if (cachedValue) {
    return {
      currency: to,
      amount: amount * cachedValue.rate,
    };
  }

  const currencyApi = new CurrencyAPI(env.VITE_CURRENCY_API_KEY);

  try {
    const result = await currencyApi.latest({
      base_currency: from,
      currencies: [to],
    });

    if (!result.data[to]) {
      throw new Error('Failed to fetch currency conversion');
    }
    const rate: number = result.data[to].value;

    setInCache(from, to, rate);

    return {
      currency: to,
      amount: amount * rate,
    };
  } catch {
    return {
      currency: from,
      amount,
    };
  }
}
